import { useEffect, useState } from 'react';
import { formatDateDDMMYYYY } from '../utils/format';
import { supabase, isSupabaseConfigured } from '../lib/supabase';
import { ResidentAccountManager } from './ResidentAccountManager';
import { DeletedResidentsHistory } from './DeletedResidentsHistory';

interface AppUser { id: string; username: string; role: 'admin'|'user'|'manager'|'resident'; password_plain?: string|null; last_login_at?: string|null; room_number?: string|null; }
interface AuditLog { id: number; created_at: string; username: string|null; action: string|null; result: string|null; details: any }
interface ResidentDob { id: string; name: string; date_of_birth: string|null }

export function AdminPanel({ visible, onClose, currentUser }: { visible: boolean; onClose: () => void; currentUser?: any }) {
  const [users, setUsers] = useState<AppUser[]>([]);
  const [newUser, setNewUser] = useState<{ username: string; password: string; role: 'user'|'manager'|'admin'|'resident'; kosType: string; roomNumber: string }>({ username: '', password: '', role: 'user', kosType: '', roomNumber: '' });
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [logUserFilter, setLogUserFilter] = useState('');
  const [birthdays, setBirthdays] = useState<ResidentDob[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string|null>(null);
  const [showResidentManager, setShowResidentManager] = useState(false);
  const [showDeletedHistory, setShowDeletedHistory] = useState(false);

  useEffect(() => {
    if (!visible) return;
    void loadUsers();
  }, [visible]);

  const loadUsers = async () => {
    try {
      setLoading(true); setError(null);
      if (!isSupabaseConfigured) throw new Error('Supabase belum dikonfigurasi');
      const { data, error } = await supabase.from('app_users').select('id, username, role, password_plain, last_login_at, room_number').order('username');
      if (error) throw error;
      setUsers((data || []) as AppUser[]);
    } catch (e:any) {
      setError(e.message || 'Gagal memuat akun');
    } finally { setLoading(false); }
  };

  const createUser = async () => {
    try {
      setLoading(true); setError(null);
      if (!newUser.username || !newUser.password) throw new Error('Username dan password wajib');
      
      // Prepare user data
      const userData: any = { 
        username: newUser.username, 
        role: newUser.role, 
        password_plain: newUser.password 
      };
      
      // Add room_number for resident role with building prefix
      if (newUser.role === 'resident' && newUser.kosType && newUser.roomNumber) {
        let roomNumber = newUser.roomNumber;
        
        // Add prefix based on building type
        if (newUser.kosType === 'gajah3' && !roomNumber.startsWith('G3-')) {
          roomNumber = `G3-${roomNumber.padStart(2, '0')}`;
        } else if (newUser.kosType === 'anduring' && !roomNumber.startsWith('AND-')) {
          roomNumber = `AND-${roomNumber.padStart(2, '0')}`;
        } else if (newUser.kosType === 'limau_manis' && !roomNumber.startsWith('LM-')) {
          roomNumber = `LM-${roomNumber.padStart(2, '0')}`;
        }
        
        userData.room_number = roomNumber;
      }
      
      const { error } = await supabase.from('app_users').insert(userData);
      if (error) throw error;
      await logAction('create_user', 'success', { username: newUser.username, role: newUser.role, room_number: userData.room_number });
      setNewUser({ username: '', password: '', role: 'user', kosType: '', roomNumber: '' });
      await loadUsers();
    } catch (e:any) {
      setError(e.message || 'Gagal membuat akun');
      await logAction('create_user', 'error', { message: e.message });
    } finally { setLoading(false); }
  };

  const logAction = async (action: string, result: string, details?: any) => {
    try { await supabase.from('audit_logs').insert({ username: 'irfan', action, result, details }); } catch {}
  };

  const loadLogs = async () => {
    try {
      setLoading(true); setError(null);
      let query = supabase.from('audit_logs').select('*').order('created_at', { ascending: false }).limit(200);
      if (logUserFilter.trim()) {
        query = query.ilike('username', `%${logUserFilter.trim()}%`);
      }
      const { data, error } = await query;
      if (error) throw error;
      setLogs((data || []) as AuditLog[]);
    } catch (e:any) {
      setError(e.message || 'Gagal memuat log');
    } finally { setLoading(false); }
  };

  const loadBirthdays = async () => {
    try {
      setLoading(true); setError(null);
      // Compute from now to +3 months
      const now = new Date(); now.setHours(0,0,0,0);
      const in3 = new Date(now); in3.setMonth(in3.getMonth() + 3);
      // Fetch residents with DOB and filter in JS (portable across DBs)
      const { data, error } = await supabase
        .from('residents')
        .select('id, name, date_of_birth')
        .not('date_of_birth', 'is', null);
      if (error) throw error;
      const list = (data as ResidentDob[] || []).filter(r => {
        if (!r.date_of_birth) return false;
        const dob = new Date(r.date_of_birth + 'T00:00:00');
        const thisYear = new Date(now.getFullYear(), dob.getMonth(), dob.getDate());
        const nextYear = new Date(now.getFullYear()+1, dob.getMonth(), dob.getDate());
        const candidate = (thisYear >= now && thisYear <= in3) ? thisYear : (nextYear >= now && nextYear <= in3 ? nextYear : null);
        return !!candidate;
      }).sort((a,b) => {
        const da = new Date(a.date_of_birth as string); const db = new Date(b.date_of_birth as string);
        return da.getMonth()*31+da.getDate() - (db.getMonth()*31+db.getDate());
      });
      setBirthdays(list);
    } catch (e:any) {
      setError(e.message || 'Gagal memuat ulang tahun');
    } finally { setLoading(false); }
  };

  if (!visible) return null;

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-900 rounded-xl shadow-xl w-full max-w-5xl p-6 overflow-y-auto max-h-[90vh]">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">Admin Panel</h2>
          <button onClick={onClose} className="px-3 py-1.5 rounded bg-gray-200 dark:bg-gray-800">Tutup</button>
        </div>

        {error && <div className="mb-3 text-red-600 text-sm">{error}</div>}

        {/* Accounts */}
        <section className="mb-6">
          <h3 className="font-semibold mb-2">Daftar Akun</h3>
          <div className="overflow-x-auto border rounded">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-800">
                <tr>
                  <th className="text-left p-2">ID</th>
                  <th className="text-left p-2">Username</th>
                  <th className="text-left p-2">Role</th>
                  <th className="text-left p-2">No. Kamar</th>
                  <th className="text-left p-2">Password (plain)</th>
                  <th className="text-left p-2">Login Terakhir</th>
                </tr>
              </thead>
              <tbody>
                {users.map(u => (
                  <tr key={u.id} className="border-t">
                    <td className="p-2">{u.id}</td>
                    <td className="p-2">{u.username}</td>
                    <td className="p-2">{u.role}</td>
                    <td className="p-2">{u.room_number || '-'}</td>
                    <td className="p-2">{u.password_plain || '-'}</td>
                    <td className="p-2">{u.last_login_at ? formatDateDDMMYYYY(u.last_login_at) : '-'}</td>
                  </tr>
                ))}
                {!users.length && (
                  <tr><td className="p-2" colSpan={6}>Tidak ada data</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </section>

        {/* Resident Account Management - Special access for Yudha */}
        {currentUser && (currentUser.username === 'yudha' || currentUser.username === 'oeoel') && (
          <section className="mb-6">
            <div className="flex justify-between items-center mb-2">
              <h3 className="font-semibold">Manajemen Akun Penghuni</h3>
              <button 
                onClick={() => setShowResidentManager(true)}
                className="px-4 py-2 rounded btn-primary"
              >
                Kelola Akun Penghuni
              </button>
            </div>
            <div className="p-4 bg-blue-50 dark:bg-blue-900/30 rounded-lg">
              <p className="text-sm text-blue-700 dark:text-blue-300">
                Fitur khusus untuk mengelola seluruh akun penghuni kos, termasuk melihat data lengkap dan mengubah password.
              </p>
            </div>
          </section>
        )}

        {/* Deleted Residents History */}
        <section className="mb-6">
          <div className="flex justify-between items-center mb-2">
            <h3 className="font-semibold">History Penghuni Terhapus</h3>
            <button 
              onClick={() => setShowDeletedHistory(true)}
              className="px-4 py-2 rounded btn-danger flex items-center gap-2"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="3 6 5 6 21 6"></polyline>
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
              </svg>
              Lihat History
            </button>
          </div>
          <div className="p-4 bg-red-50 dark:bg-red-900/30 rounded-lg">
            <p className="text-sm text-red-700 dark:text-red-300">
              Backup data penghuni dan pembayaran yang telah dihapus. Semua data penghuni yang dihapus akan otomatis dicadangkan ke history.
            </p>
          </div>
        </section>

        {/* Create User */}
        <section className="mb-6">
          <h3 className="font-semibold mb-2">Buat Akun Pengguna</h3>
          <div className="grid grid-cols-1 md:grid-cols-6 gap-2 items-end">
            <div>
              <label className="block text-xs mb-1">ID Pengguna</label>
              <input value={newUser.username} onChange={e=>setNewUser(v=>({...v, username:e.target.value}))} className="w-full p-2 border rounded bg-white dark:bg-gray-800" />
            </div>
            <div>
              <label className="block text-xs mb-1">Kata Sandi</label>
              <input type="text" value={newUser.password} onChange={e=>setNewUser(v=>({...v, password:e.target.value}))} className="w-full p-2 border rounded bg-white dark:bg-gray-800" />
            </div>
            <div>
              <label className="block text-xs mb-1">Role</label>
              <select value={newUser.role} onChange={e=>setNewUser(v=>({...v, role:e.target.value as any}))} className="w-full p-2 border rounded bg-white dark:bg-gray-800">
                <option value="user">user</option>
                <option value="manager">manager</option>
                <option value="admin">admin</option>
                <option value="resident">resident</option>
              </select>
            </div>
            {newUser.role === 'resident' && (
              <>
                <div>
                  <label className="block text-xs mb-1">Pilih Kos</label>
                  <select value={newUser.kosType} onChange={e=>setNewUser(v=>({...v, kosType:e.target.value}))} className="w-full p-2 border rounded bg-white dark:bg-gray-800">
                    <option value="">Pilih Kos</option>
                    <option value="gajah4">Kos Gajah 4</option>
                    <option value="gajah3">Kos Gajah 3</option>
                    <option value="anduring">Kos Anduring</option>
                    <option value="limau_manis">Kos Limau Manis</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs mb-1">No. Kamar</label>
                  <input 
                    type="text" 
                    value={newUser.roomNumber} 
                    onChange={e=>setNewUser(v=>({...v, roomNumber:e.target.value}))} 
                    placeholder={newUser.kosType === 'gajah4' ? 'e.g: 101' : newUser.kosType === 'gajah3' ? 'e.g: 01' : newUser.kosType === 'anduring' ? 'e.g: 01' : newUser.kosType === 'limau_manis' ? 'e.g: 01' : 'No. Kamar'}
                    className="w-full p-2 border rounded bg-white dark:bg-gray-800" 
                  />
                </div>
              </>
            )}
            <div className={newUser.role === 'resident' ? 'md:col-span-1' : 'md:col-span-3'}>
              <button onClick={createUser} disabled={loading || (newUser.role === 'resident' && (!newUser.kosType || !newUser.roomNumber))} className="w-full p-2 rounded btn-primary disabled:opacity-50">Buat Akun</button>
            </div>
          </div>
          {newUser.role === 'resident' && (
            <div className="mt-2 text-xs text-gray-600 dark:text-gray-400">
              <p>• Untuk Gajah 4: Masukkan nomor kamar langsung (contoh: 101, 205)</p>
              <p>• Untuk Gajah 3: Akan otomatis ditambah prefix G3- (contoh: 01 → G3-01)</p>
              <p>• Untuk Anduring: Akan otomatis ditambah prefix AND- (contoh: 01 → AND-01)</p>
              <p>• Untuk Limau Manis: Akan otomatis ditambah prefix LM- (contoh: 01 → LM-01)</p>
            </div>
          )}
        </section>

        {/* Audit Logs */}
        <section className="mb-6">
          <div className="flex items-end justify-between mb-2 gap-2">
            <h3 className="font-semibold">Catatan Tindakan (Audit Log)</h3>
            <div className="flex gap-2">
              <input placeholder="Filter username" value={logUserFilter} onChange={e=>setLogUserFilter(e.target.value)} className="p-2 border rounded bg-white dark:bg-gray-800" />
              <button onClick={loadLogs} className="px-3 py-2 rounded bg-gray-200 dark:bg-gray-800">Muat Catatan</button>
            </div>
          </div>
          <div className="overflow-x-auto border rounded max-h-80">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-800 sticky top-0">
                <tr>
                  <th className="text-left p-2">Waktu</th>
                  <th className="text-left p-2">Pengguna</th>
                  <th className="text-left p-2">Aksi</th>
                  <th className="text-left p-2">Hasil</th>
                  <th className="text-left p-2">Detail</th>
                </tr>
              </thead>
              <tbody>
                {logs.map(l => (
                  <tr key={l.id} className="border-t">
                    <td className="p-2">{formatDateDDMMYYYY(l.created_at)}</td>
                    <td className="p-2">{l.username || '-'}</td>
                    <td className="p-2">{l.action || '-'}</td>
                    <td className="p-2">{l.result || '-'}</td>
                    <td className="p-2 whitespace-pre-wrap text-xs">{l.details ? JSON.stringify(l.details) : '-'}</td>
                  </tr>
                ))}
                {!logs.length && (
                  <tr><td className="p-2" colSpan={5}>Tidak ada data</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </section>

        {/* Birthdays */}
        <section>
          <div className="flex items-end justify-between mb-2 gap-2">
            <h3 className="font-semibold">Ulang Tahun Bulan Ini s/d 3 Bulan ke Depan</h3>
            <button onClick={loadBirthdays} className="px-3 py-2 rounded bg-gray-200 dark:bg-gray-800">Muat Daftar Ulang Tahun</button>
          </div>
          <div className="overflow-x-auto border rounded">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-800">
                <tr>
                  <th className="text-left p-2">Nama</th>
                  <th className="text-left p-2">Tanggal Lahir</th>
                </tr>
              </thead>
              <tbody>
                {birthdays.map(b => (
                  <tr key={b.id} className="border-t">
                    <td className="p-2">{b.name}</td>
                    <td className="p-2">{b.date_of_birth ? formatDateDDMMYYYY(b.date_of_birth) : '-'}</td>
                  </tr>
                ))}
                {!birthdays.length && (
                  <tr><td className="p-2" colSpan={2}>Tidak ada data</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      </div>
      
      {/* Resident Account Manager Modal */}
      {currentUser && (currentUser.username === 'yudha' || currentUser.username === 'oeoel') && (
        <ResidentAccountManager 
          visible={showResidentManager} 
          onClose={() => setShowResidentManager(false)} 
        />
      )}
      
      {/* Deleted Residents History Modal */}
      <DeletedResidentsHistory 
        visible={showDeletedHistory} 
        onClose={() => setShowDeletedHistory(false)} 
      />
    </div>
  );
}
